import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import Presentation, {
  PresentationResponseData,
} from "../../core/models/Presentation";
import NetworkingServiceError from "../../core/services/network/NetworkServiceError";
import appConfig from "../../util/appConfig";

export type PresentationError = NetworkingServiceError;

export type PresentationsDataType = {
  presentationResponseData?: PresentationResponseData;
  setPresentationResponseData: (
    responseData?: PresentationResponseData
  ) => void;
  presentationLoading: boolean;
  selectedPresentation?: Presentation;
  setPresentationLoading: (isLoading: boolean) => void;
  presentationError: PresentationError | undefined;
  setPresentationError: (error?: PresentationError) => void;
  setSelectedPresentation: (presentation?: Presentation) => void;
};

export const PresentationsDataContext = createContext<
  PresentationsDataType | undefined
>(undefined);

export const PresentationsDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [presentationResponseData, setPresentationResponseData] =
    useState<PresentationResponseData>();
  const [presentationLoading, setPresentationLoading] =
    useState<boolean>(false);
  const [presentationError, setPresentationError] =
    useState<PresentationError>();
  const [selectedPresentation, setSelectedPresentation] =
    useState<Presentation>();

  const value = {
    presentationResponseData,
    setPresentationResponseData,
    presentationLoading,
    setPresentationLoading,
    presentationError,
    setPresentationError,
    selectedPresentation,
    setSelectedPresentation,
  };

  useEffect(() => {
    if (appConfig.debugPresentations && selectedPresentation)
      console.debug(
        "[Presentations][SelectedPresentation] ",
        selectedPresentation
      );
  }, [selectedPresentation]);

  useEffect(() => {
    if (appConfig.debugPresentations && presentationResponseData)
      console.debug(
        "[Presentations][PresentationResponseData]",
        presentationResponseData
      );
  }, [presentationResponseData]);

  useEffect(() => {
    if (appConfig.debugPresentations && presentationLoading)
      console.debug(
        "[Presentations][PresentationLoading]",
        presentationLoading
      );
  }, [presentationLoading]);

  useEffect(() => {
    if (appConfig.debugPresentations && presentationError)
      console.warn(
        "[Presentations][Error]",
        presentationError?.userMessage?.(),
        presentationError?.debugMessage?.()
      );
  }, [presentationError]);

  return (
    <PresentationsDataContext.Provider value={value}>
      {children}
    </PresentationsDataContext.Provider>
  );
};

export const usePresentationsContext = () => {
  const context = useContext(PresentationsDataContext);
  if (!context) {
    throw new Error(
      "usePresentationsContext must be used within a PresentationsDataProvider"
    );
  }
  return context;
};
